import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAstrologerProfile } from '../../hooks/useAstrologerProfile';
import { useToast } from '../../components/ui/ToastSystem';
import {
  FEEDBACK_CATEGORY_LABELS,
  FEEDBACK_STATUS_LABELS,
  PRO_SERVICES,
} from '../../types/professional';
import type { AstrologerFeedback, FeedbackCategory } from '../../types/professional';
import { Plus, X, ChevronDown, ChevronUp } from 'lucide-react';

const CATEGORIES = Object.entries(FEEDBACK_CATEGORY_LABELS) as [FeedbackCategory, string][];

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function ProfessionalFeedback() {
  const { astrologerProfile } = useAstrologerProfile();
  const { showToast } = useToast();

  const [items, setItems] = useState<AstrologerFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Form fields
  const [category, setCategory] = useState<FeedbackCategory>('sugestao_funcionalidade');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [serviceSlug, setServiceSlug] = useState('');

  useEffect(() => {
    if (!astrologerProfile) return;
    supabase
      .from('astrologer_feedback')
      .select('*')
      .eq('astrologer_id', astrologerProfile.id)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        setItems((data ?? []) as AstrologerFeedback[]);
        setLoading(false);
      });
  }, [astrologerProfile?.id]);

  function resetForm() {
    setCategory('sugestao_funcionalidade');
    setSubject('');
    setDescription('');
    setServiceSlug('');
    setShowForm(false);
  }

  async function handleSubmit() {
    if (!astrologerProfile || !subject.trim() || !description.trim()) return;
    setSubmitting(true);
    const { data, error } = await supabase
      .from('astrologer_feedback')
      .insert({
        astrologer_id: astrologerProfile.id,
        category,
        subject: subject.trim(),
        description: description.trim(),
        service_slug: serviceSlug || null,
      })
      .select()
      .single();

    if (error) {
      showToast({ type: 'error', message: 'Erro ao enviar feedback. Tente novamente.' });
    } else {
      setItems(prev => [data as AstrologerFeedback, ...prev]);
      showToast({ type: 'success', message: 'Feedback enviado com sucesso!' });
      resetForm();
    }
    setSubmitting(false);
  }

  const cardStyle: React.CSSProperties = {
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '10px',
    padding: '1.25rem',
    marginBottom: '0.75rem',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'var(--bg-input)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '8px',
    padding: '0.625rem 0.75rem',
    color: 'var(--text-primary)',
    fontSize: 'var(--text-body)',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 'var(--text-caption)',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    marginBottom: '0.375rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: '2rem' }}>Carregando…</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 'var(--text-h3)', fontWeight: 700 }}>Feedback & Sugestões</h2>
          <p style={{ margin: '0.25rem 0 0', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
            Ajude a melhorar o Professya. Veja o status das suas sugestões anteriores.
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              background: 'var(--petrol)', color: '#fff',
              border: 'none', borderRadius: '8px',
              padding: '0.5rem 1rem', fontSize: 'var(--text-caption)',
              fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            <Plus size={15} /> Novo Feedback
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div style={{ ...cardStyle, marginBottom: '1.5rem', borderColor: 'var(--petrol)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h3 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 700 }}>Novo Feedback</h3>
            <button onClick={resetForm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.25rem' }}>
              <X size={18} />
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={labelStyle}>Categoria</label>
              <select value={category} onChange={e => setCategory(e.target.value as FeedbackCategory)} style={inputStyle}>
                {CATEGORIES.map(([val, lbl]) => (
                  <option key={val} value={val}>{lbl}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Serviço relacionado (opcional)</label>
              <select value={serviceSlug} onChange={e => setServiceSlug(e.target.value)} style={inputStyle}>
                <option value="">— Nenhum serviço específico —</option>
                {PRO_SERVICES.map(s => (
                  <option key={s.slug} value={s.slug}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Assunto</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Resumo em uma linha..."
              maxLength={120}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={labelStyle}>Descrição</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Descreva o problema, sugestão ou melhoria com o máximo de detalhes possível..."
              rows={5}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              onClick={resetForm}
              style={{ background: 'none', border: '1px solid var(--border-subtle)', borderRadius: '8px', padding: '0.5rem 1rem', color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', cursor: 'pointer', fontWeight: 500 }}
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !subject.trim() || !description.trim()}
              style={{
                background: submitting || !subject.trim() || !description.trim() ? 'var(--border-subtle)' : 'var(--petrol)',
                color: '#fff', border: 'none', borderRadius: '8px',
                padding: '0.5rem 1.25rem', fontSize: 'var(--text-caption)',
                fontWeight: 600, cursor: submitting ? 'wait' : 'pointer',
              }}
            >
              {submitting ? 'Enviando…' : 'Enviar Feedback'}
            </button>
          </div>
        </div>
      )}

      {/* Feedback list */}
      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-muted)' }}>
          <p style={{ fontSize: '2rem', margin: '0 0 0.5rem' }}>💬</p>
          <p style={{ margin: 0 }}>Nenhum feedback enviado ainda. Sua opinião é muito valiosa!</p>
        </div>
      ) : (
        <div>
          {items.map(item => {
            const statusInfo = FEEDBACK_STATUS_LABELS[item.status];
            const expanded = expandedId === item.id;
            return (
              <div key={item.id} style={cardStyle}>
                <div
                  onClick={() => setExpandedId(expanded ? null : item.id)}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
                      <span style={{
                        fontSize: '11px', fontWeight: 700, padding: '2px 8px',
                        borderRadius: '999px', background: 'var(--bg-surface)',
                        color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)',
                        whiteSpace: 'nowrap',
                      }}>
                        {FEEDBACK_CATEGORY_LABELS[item.category]}
                      </span>
                      <span style={{
                        fontSize: '11px', fontWeight: 700, padding: '2px 8px',
                        borderRadius: '999px', color: statusInfo.color,
                        border: `1px solid ${statusInfo.color}`,
                        background: 'transparent', whiteSpace: 'nowrap',
                      }}>
                        {statusInfo.label}
                      </span>
                      {item.service_slug && (
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                          · {PRO_SERVICES.find(s => s.slug === item.service_slug)?.name ?? item.service_slug}
                        </span>
                      )}
                    </div>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--text-body)' }}>{item.subject}</p>
                    <p style={{ margin: '0.15rem 0 0', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                      {formatDate(item.created_at)}
                    </p>
                  </div>
                  <span style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: '0.1rem' }}>
                    {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </span>
                </div>

                {expanded && (
                  <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-subtle)' }}>
                    <p style={{ margin: '0 0 0.5rem', fontSize: 'var(--text-caption)', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Descrição</p>
                    <p style={{ margin: 0, fontSize: 'var(--text-body)', lineHeight: 1.6, whiteSpace: 'pre-wrap', color: 'var(--text-primary)' }}>
                      {item.description}
                    </p>
                    {item.admin_notes && (
                      <div style={{ marginTop: '1rem', padding: '0.875rem', background: 'var(--bg-surface)', borderRadius: '8px', borderLeft: '3px solid var(--petrol)' }}>
                        <p style={{ margin: '0 0 0.35rem', fontSize: 'var(--text-caption)', fontWeight: 700, color: 'var(--petrol)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Resposta da Equipe Professya</p>
                        <p style={{ margin: 0, fontSize: 'var(--text-body)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{item.admin_notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
